
import { Logger } from '@/util/logger';

/**
 *
 * @class Easing
 *
 * @brief this is a collection of easing functions we can use for smoothing animations or
 * "warping" the way we interpolate between two positions.  For example how we zoom the
 * camera from all the way out, to all the way in.  Can be used for any kind of animation
 * though.
 *
 * We're basically using the functions from here:
 *
 *   https://github.com/Michaelangel007/easing
 *
 * Related reading:
 *
 *   https://marcgreig.github.io/Easing-and-Splines-Research/
 *   https://easings.net/
 *   https://spicyyoghurt.com/tools/easing-functions
 *   https://developer.mozilla.org/en-US/docs/Web/CSS/easing-function
 *
 * The easing functions are encoded as progress in and progress out; that is, you
 * provide a progress value (0.0 ... 1.0) and the return value is "warped" to
 * reflect where progress would be after the easing is applied which will be a
 * progress value as well (0.0 ... 1.0).  You can then scale the progress value
 * to be whatever you need it to be.
 *
 * We also add lookup table support, so we can to "reverse lookup" and pre-calculate
 * the values for speed.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      June 2023
 *
 */

/** the kinds of easing functions */

export enum EasingType {
  // noinspection JSUnusedGlobalSymbols,SpellCheckingInspection
  NONE             = 'none',
  LINEAR           = 'linear',
  IN_QUADRATIC     = 'in_quadratic',
  IN_CUBIC         = 'in_cubic',
  IN_QUARTIC       = 'in_quartic',
  IN_QUINTIC       = 'in_quintic',
  IN_SEXTIC        = 'in_sextic',
  IN_SEPTIC        = 'in_septic',
  IN_OCTIC         = 'in_octic',
  OUT_QUADRATIC    = 'out_quadratic',
  OUT_CUBIC        = 'out_cubic',
  OUT_QUARTIC      = 'out_quartic',
  OUT_QUINTIC      = 'out_quintic',
  OUT_SEXTIC       = 'out_sextic',
  OUT_SEPTIC       = 'out_septic',
  OUT_OCTIC        = 'out_octic',
  IN_OUT_QUADRATIC = 'in_out_quadratic',
  IN_OUT_CUBIC     = 'in_out_cubic',
  IN_OUT_QUARTIC   = 'in_out_quartic',
  IN_OUT_QUINTIC   = 'in_out_quintic',
  IN_OUT_SEXTIC    = 'in_out_sextic',
  IN_OUT_SEPTIC    = 'in_out_septic',
  IN_OUT_OCTIC     = 'in_out_octic',
  IN_BACK          = 'in_back',
  IN_OUT_BACK      = 'in_out_back',
  OUT_BACK         = 'out_back',
  IN_BOUNCE        = 'in_bounce',
  IN_OUT_BOUNCE    = 'in_out_bounce',
  OUT_BOUNCE       = 'out_bounce',
  IN_CIRCLE        = 'in_circle',
  IN_OUT_CIRCLE    = 'in_out_circle',
  OUT_CIRCLE       = 'out_circle',
  IN_ELASTIC       = 'in_elastic',
  IN_OUT_ELASTIC   = 'in_out_elastic',
  OUT_ELASTIC      = 'out_elastic',
  IN_EXPONENT2     = 'in_exponent2',
  IN_OUT_EXPONENT2 = 'in_out_exponent2',
  OUT_EXPONENT2    = 'out_exponent2',
  IN_SINE          = 'in_sine',
  IN_OUT_SINE      = 'in_out_sine',
  OUT_SINE         = 'out_sine',
  IN_EXPONENTE     = 'in_exponente',
  IN_OUT_EXPONENTE = 'in_out_exponente',
  OUT_EXPONENTE    = 'out_exponente',
  IN_LOG10         = 'in_log10',
  IN_OUT_LOG10     = 'in_out_log10',
  OUT_LOG10        = 'out_log10',
  IN_SQRT          = 'in_sqrt',
  IN_OUT_SQRT      = 'in_out_sqrt',
  OUT_SQRT         = 'out_sqrt',
  SMOOTHSTEP       = 'smoothstep',
  NUM              = 'num'
}

/**
 * Easing functions
 *
 * Given a normalized elapsed time (between 0.0 and 1.0 inclusive)
 * an easing function returns an adjusted, or 'warped', elapsed time
 * For example, a `Linear` function returns the time "as-is".
 * An `Out Quadratic` easing quickly accelerates then decelerates
 * as it closes to the final total time.
 *
 * @param progress - Normalized Standard Time Percentage [0.0,1.0] (inclusive)
 * @returns - Normalized Adjusted Time Percentage
 *
 * @Notes:
 *
 *   Distance = Velocity*Time
 *   d = v*t
 *
 *   Position = Start + (Finish-Start)*(Elapsed/Duration)
 *
 * Or
 *
 *   Position = Min   + (Max-Min)*(Elapsed/Duration)
 *
 */
export class Easing {

  /** the actual easing functions */
  static functions: ((progress: number) => number)[] = [

    /* 0 */

    (): number => {

      /* NONE            */

      return 1;
    },

    (p: number): number => {

      /* LINEAR          */

      return p;
    },

    (p: number): number => {

      /* IN_QUADRATIC    */

      return p*p;
    },

    (p: number): number => {

      /* IN_CUBIC        */

      return p*p*p;
    },

    (p: number): number => {

      /* IN_QUARTIC      */

      return p*p*p*p;
    },

    (p: number): number =>  {

      /* IN_QUINTIC      */

      return p*p*p*p*p;
    },

    (p: number): number =>  {

      /* IN_SEXTIC       */

      return p*p*p*p*p*p;
    },

    (p: number): number =>  {

      /* IN_SEPTIC       */

      return p*p*p*p*p*p*p;
    },

    (p: number): number =>  {

      /* IN_OCTIC        */

      return p*p*p*p*p*p*p*p;
    },

    (p: number): number =>  {

      /* OUT_QUADRATIC  */

      let m: number = p-1;

      return 1 - m * m;
    },

    /* 10 */

    (p: number): number => {

      /* OUT_CUBIC       */

      let m: number=p-1;

      return 1+m*m*m;
    },
    (p: number): number => {

      /* OUT_QUARTIC     */

      let m: number=p-1;

      return 1-m*m*m*m;
    },
    (p: number): number => {

      /* OUT_QUINTIC     */

      let m: number=p-1;

      return 1+m*m*m*m*m;
    },
    (p: number): number => {

      /* OUT_SEXTIC      */

      let m: number=p-1;

      return 1-m*m*m*m*m*m;
    },
    (p: number): number => {

      /* OUT_SEPTIC      */

      let m: number=p-1;

      return 1+m*m*m*m*m*m*m;
    },
    (p: number): number => {

      /* OUT_OCTIC       */

      let m: number=p-1;

      return 1-m*m*m*m*m*m*m*m;
    },
    (p: number): number => {

      /* IN_OUT_QUADRATIC */

      let m: number=p-1,t: number=p*2;

      if (t < 1) {

        return p*t;
      }

      return 1-m*m * 2;
    },
    (p: number): number => {

      /* IN_OUT_CUBIC    */

      let m: number=p-1,t: number=p*2;

      if (t < 1) {
        return p*t*t;
      }

      return 1+m*m*m * 4;
    },
    (p: number): number => {

      /* IN_OUT_QUARTIC  */

      let m: number=p-1,t: number=p*2;

      if (t < 1) {
        return p*t*t*t;
      }

      return 1-m*m*m*m * 8;
    },
    (p: number): number => {

      /* IN_OUT_QUINTIC  */

      let m: number=p-1,t: number=p*2;

      if (t < 1) {

        return p*t*t*t*t;
      }

      return 1+m*m*m*m*m * 16;
    },

    /* 20 */

    (p: number): number => {

      /* IN_OUT_SEXTIC   */

      let m: number=p-1,t: number=p*2;

      if (t < 1) {

        return p*t*t*t*t*t;
      }

      return 1-m*m*m*m*m*m    * 32;
    },

    (p: number): number => {

      /* IN_OUT_SEPTIC   */

      let m: number=p-1,t: number=p*2;

      if (t < 1) {

        return p*t*t*t*t*t*t;
      }

      return 1+m*m*m*m*m*m*m  * 64;
    },

    (p: number): number => {

      /* IN_OUT_OCTIC    */

      let m: number=p-1,t: number=p*2;

      if (t < 1) {

        return p*t*t*t*t*t*t*t;
      }

      return 1-m*m*m*m*m*m*m*m*128;
    },

    (p: number): number => {

      /* IN_BACK         */

      let              k: number = 1.70158;

      return p*p*(p*(k+1) - k);
    },

    (p: number): number => {

      /* IN_OUT_BACK     */

      let m: number=p-1,t: number=p*2, k: number = 1.70158 * 1.525;

      if (p < 0.5) {

        return p*t*(t*(k+1) - k);
      }

      return 1 + 2*m*m*(2*m*(k+1) + k);
    },

    (p: number): number => {

      /* OUT_BACK        */

      let m: number=p-1, k: number = 1.70158;

      return 1 + m*m*(  m*(k+1) + k);
    },

    (p: number): number => {

      /* IN_BOUNCE       */

      return 1 - Easing.ease(EasingType.OUT_BOUNCE,  1-p );
    },

    (p: number): number => {

      /* IN_OUT_BOUNCE   */

      let t: number = p*2;

      if (t < 1) {

        return 0.5 - 0.5*Easing.ease(EasingType.OUT_BOUNCE,  1 - t );
      }

      return 0.5 + 0.5*Easing.ease(EasingType.OUT_BOUNCE,  t - 1 );
    },

    (p: number): number => {

      /* OUT_BOUNCE      */

      let r: number  = 1  / 2.75; // reciprocal
      let k1: number =         r; // 36.36%
      let k2: number = 2     * r; // 72.72%
      let k3: number = 1.5   * r; // 54.54%
      let k4: number = 2.5   * r; // 90.90%
      let k5: number = 2.25  * r; // 81.81%
      let k6: number = 2.625 * r; // 95.45%
      let k0: number = 7.5625, t;

      if (p < k1) {

        return k0 * p*p;

      } else if (p < k2) {

        t = p - k3;

        return k0 * t*t + 0.75;     // 48/64

      } else if (p < k4) {

        t = p - k5;

        return k0 * t*t + 0.9375;   // 60/64

      }

      t = p - k6;

      return k0 * t*t + 0.984375; // 63/64}
    },

    (p: number): number => {

      /* IN_CIRCLE       */

      return 1-Math.sqrt( 1 - p*p );
    },

    /* 30 */

    (p: number): number => {

      /* IN_OUT_CIRCLE   */

      let m: number=p-1,t: number=p*2;

      if (t < 1) {

        return (1-Math.sqrt( 1 - t*t ))*0.5;
      }

      return (Math.sqrt( 1 - 4*m*m ) + 1) * 0.5;
    },

    (p: number): number => {

      /* OUT_CIRCLE      */

      let m: number=p-1;

      return  Math.sqrt( 1 - m*m );
    },

    (p: number): number => {

      /* IN_ELASTIC      */

      let m: number = p-1;

      return - Math.pow( 2,10*m ) * Math.sin( ( m*40 - 3) * Math.PI/6);
    },

    (p: number): number => {

      /* OUT_ELASTIC     */

      let s: number = 2*p-1;                 // remap: [0,0.5] -> [-1,0]
      let k: number = (80*s-9) * Math.PI/18; // and    [0.5,1] -> [0,+1]

      if (s < 0) {

        return   -0.5*Math.pow(2, 10*s) * Math.sin( k );
      }

      return 1 +0.5*Math.pow(2,-10*s) * Math.sin( k );
    },

    (p: number): number => {

      /* OUT_ELASTIC     */

      return 1+(Math.pow( 2,10*-p ) * Math.sin( (-p*40 - 3) * Math.PI/6 ));
    },

    (p: number): number => {

      /* IN_EXPONENT2    */

      if (p <= 0) {

        return 0;
      }

      return Math.pow( 2, 10*(p-1) );
    },

    (p: number): number => {

      /* IN_OUT_EXPONENT2 */

      if (p <= 0) {

        return 0;
      }

      if (p >= 1) {

        return 1;
      }

      if (p <0.5) {

        return Math.pow( 2, 10*(2*p-1)-1);
      }

      return 1-Math.pow( 2, -10*(2*p-1)-1);
    },

    (p: number): number => {

      /* OUT_EXPONENT2   */

      if (p >= 1) {

        return 1;
      }

      return 1-Math.pow( 2, -10* p);
    },

    (p: number): number => {

      /* IN_SINE         */

      return 1 - Math.cos( p * Math.PI*0.5 );
    },

    (p: number): number => {

      /* IN_OUT_SINE     */

      return 0.5*(1 - Math.cos( p * Math.PI ));
    },

    /* 40 */

    (p: number): number => {

      /* OUT_SINE        */

      return Math.sin( p * Math.PI*0.5 );
    },

    (p: number): number => {

      /* IN_EXPONENTE    */

      if (p <= 0) {

        return 0;
      }

      return Math.pow( Math.E, -10*(1-p) );  // Scale 0..1 -> p^-10 .. p^0
    },

    (p: number): number => {

      /* IN_OUT_EXPONENTE */

      let t = p*2;

      if (t < 1) {

        return 0.5 - 0.5*Easing.ease(EasingType.OUT_EXPONENTE,  1 - t );
      }

      return 0.5 + 0.5*Easing.ease(EasingType.OUT_EXPONENTE,  t - 1 );
    },

    (p: number): number => {

      /* OUT_EXPONENTE   */

      return 1 - Easing.ease(EasingType.IN_EXPONENTE, 1-p );
    },

    (p: number): number => {

      /* IN_LOG10        */

      return 1 - Easing.ease(EasingType.OUT_LOG10,  1-p );
    },

    (p: number): number => {

      /* IN_OUT_LOG10    */

      let t: number = p*2;

      if (t < 1) {

        return 0.5 - 0.5*Easing.ease(EasingType.OUT_LOG10, 1 - t );
      }

      return 0.5 + 0.5*Easing.ease(EasingType.OUT_LOG10, t - 1 );
    },

    (p: number): number => {

      /* OUT_LOG10       */

      return Math.log10( (p*9)+1 ); // Scale 0..1 -> Log10( 1 ) .. Log10( 10 )
    },

    (p: number): number => {

      /* IN_SQRT         */

      return 1 - Easing.ease(EasingType.OUT_SQRT, 1-p );
    },

    (p: number): number => {

      /* IN_OUT_SQRT     */

      let t: number = p*2;

      if (t < 1) {

        return 0.5 - 0.5*Easing.ease(EasingType.OUT_SQRT, 1 - t );
      }

      return 0.5 + 0.5*Easing.ease(EasingType.OUT_SQRT, t - 1 );
    },

    (p: number): number => {

      /* OUT_SQRT        */

      return Math.sqrt( p )
    },

    /* 50 */

    (p: number): number => {

      /* SMOOTHSTEP      */

      if ( p < 0 ) {

        p = 0;
      }

      if ( p > 1 ) {

        p = 1;
      }

      return p*p*(3-2*p);
    },
  ];

  /** fetch the index to the easing function of this kind */
  private static index(kind: EasingType): number {

    let k = kind.toString().toLowerCase();

    // noinspection JSUnusedGlobalSymbols,SpellCheckingInspection
    switch (k) {
      case 'none':             return 0;
      case 'linear':           return 1;
      case 'in_quadratic':     return 2;
      case 'in_cubic':         return 3;
      case 'in_quartic':       return 4;
      case 'in_quintic':       return 5;
      case 'in_sextic':        return 6;
      case 'in_septic':        return 7;
      case 'in_octic':         return 8;
      case 'out_quadratic':    return 9;
      case 'out_cubic':        return 10;
      case 'out_quartic':      return 11;
      case 'out_quintic':      return 12;
      case 'out_sextic':       return 13;
      case 'out_septic':       return 14;
      case 'out_octic':        return 15;
      case 'in_out_quadratic': return 16;
      case 'in_out_cubic':     return 17;
      case 'in_out_quartic':   return 18;
      case 'in_out_quintic':   return 19;
      case  'in_out_sextic':   return 20;
      case  'in_out_septic':   return 21;
      case 'in_out_octic':     return 22;
      case 'in_back':          return 23;
      case 'in_out_back':      return 24;
      case 'out_back':         return 25;
      case 'in_bounce':        return 26;
      case 'in_out_bounce':    return 27;
      case 'out_bounce':       return 28;
      case 'in_circle':        return 29;
      case 'in_out_circle':    return 30;
      case 'out_circle':       return 31;
      case 'in_elastic':       return 32;
      case 'in_out_elastic':   return 33;
      case 'out_elastic':      return 34;
      case 'in_exponent2':     return 35;
      case 'in_out_exponent2': return 36;
      case 'out_exponent2':    return 37;
      case 'in_sine':          return 38;
      case 'in_out_sine':      return 39;
      case 'out_sine':         return 40;
      case 'in_exponente':     return 41;
      case 'in_out_exponente': return 42;
      case 'out_exponente':    return 43;
      case 'in_log10':         return 44;
      case 'in_out_log10':     return 45;
      case 'out_log10':        return 46;
      case 'in_sqrt':          return 47;
      case 'in_out_sqrt':      return 48;
      case 'out_sqrt':         return 49;
      case 'smoothstep':       return 50;
      case 'num':              return 51;
    }

    /* if not found just return the pass through function index */

    return 0;
  }

  /**
   * given a current the "warped" or "tweened" version of the progress.
   *
   * @param kind which easing curve to use
   *
   * @param progress a normalized progress value between 0.0 and 1.0.
   *
   * @return a corresponding tweened value between 0.0 and 1.0.
   */
  public static ease(kind: EasingType, progress: number): number {

    if (progress < 0.0) {

      progress = 0.0;
    }

    if (progress > 1.0) {

      progress = 1.0;
    }

    /* tween it! */

    return Easing.functions[Easing.index(kind)](progress);
  }
}
